import { subscribeAppResume } from "../services/appResumeService";
import { useState, useRef } from "react";
import { apiRequest, apiRequestWithRetry, isNetworkError } from "../services/apiClient";
import { belgeleriDinleService } from "../services/documentService";
import { konumlariDinleService } from "../services/haritaService";
import { odalariDinleService } from "../services/chatService";
import { varsayilanAlisverisKategorileri } from "../utils/constants";
import { dataSyncService, isDataSyncConnected } from "../services/dataSyncService";
import { rt } from "../i18n/runtime";
import {
  areAndroidHealthFeaturesDisabled,
  isAndroidRestrictedModule,
} from "../utils/platformFeatures";
import {
  nextConnectedReconcileDelay,
  nextDisconnectedSyncDelay,
} from "../utils/syncPollingPolicy";
import { readSensitiveItem, removeSensitiveItem, writeSensitiveItem } from "../utils/sensitiveSessionStorage";

// Hook dışı cache okuyucu — useState lazy init için familyId ile erişir
const _readDC = (familyId, resource) => {
  try {
    const r = readSensitiveItem(`fp:dc:${familyId}:${resource}`);
    return r ? JSON.parse(r) : null;
  } catch { return null; }
};

const _hasDashboardCache = (familyId) => {
  if (!familyId) return false;
  return ["shopping", "tasks", "finance", "sharedExpenses", "calendar", "health", "children"]
    .filter((resource) => !isAndroidRestrictedModule(resource))
    .every((resource) => _readDC(familyId, resource) !== null);
};

// Para birimi kodunu sembole çevirir — bildirim metinlerinde tutarın yanında
// kullanılır. Sembol evrensel olduğu için (₺/$/€) dil bağımsızdır, hem TR hem
// EN bildirimde aynı şekilde görünür.
const PARA_BIRIMI_SEMBOL = { TRY: "₺", USD: "$", EUR: "€" };
const tutarFormatla = (tutar, paraBirimi) => {
  const sembol = PARA_BIRIMI_SEMBOL[paraBirimi] || PARA_BIRIMI_SEMBOL.TRY;
  const sayi = Number(tutar) || 0;
  // Türkçe ondalık ayracı (virgül) kullanılmaz — sembol zaten dile bağlı değil,
  // sade bir sayı formatı (1500, 1500.5) her iki dilde de net okunur.
  const sayiStr = Number.isInteger(sayi) ? String(sayi) : sayi.toFixed(2);
  return `${sayiStr} ${sembol}`;
};

/**
 * Tüm aile verilerini state olarak döndürür.
 * Firebase Firestore listener kullanılmaz.
 * Tüm ana dashboard datası backend REST/polling/SSE servislerinden beslenir.
 */
export function useAppData({ familyId, user, bildirimGoster, bildirimHerkeseYazService,
  bildirimEkleService, uyelerRef, aktifSayfaRef }) {
  const currentUserUid = String(user?.uid || user?.firebase_uid || user?.id || "").trim();

  const msg = (key, vars) => rt(key, vars);
  const shoppingAlindiDurumRef = useRef({});
  const forceFreshNextLoadRef = useRef(null);
  // Yalnızca halen aktif olan kullanıcı/aile yüklemesi loading durumunu kapatabilir.
  // Hesap veya aile değişiminde eski REST cevaplarının yeni ekranı hazır göstermesini önler.
  const activeInitialLoadRef = useRef("");

  // ── Offline data cache yardımcıları ──────────────────────────────────────
  // Her kaynak kendi key'iyle saklanır; familyId prefix'i ile izole edilir.
  const cacheKey = (resource) => `fp:dc:${familyId}:${resource}`;
  const writeCache = (resource, data) => {
    try { writeSensitiveItem(cacheKey(resource), JSON.stringify(data)); } catch {}
  };
  const readCache = (resource) => {
    try { const r = readSensitiveItem(cacheKey(resource)); return r ? JSON.parse(r) : null; }
    catch { return null; }
  };

  // State'leri cache'den başlat — familyId varsa anında son veriyi göster
  const [urunler, setUrunler]                         = useState(() => _readDC(familyId, "shopping") || []);
  const [alisverisKategorileri, setAlisverisKategorileri] = useState(varsayilanAlisverisKategorileri);
  const [gorevler, setGorevler]                       = useState(() => _readDC(familyId, "tasks") || []);
  const [finanslar, setFinanslar]                     = useState(() => _readDC(familyId, "finance") || []);
  const [harcamalar, setHarcamalar]                   = useState(() => _readDC(familyId, "sharedExpenses") || []);
  const [takvimEtkinlikleri, setTakvimEtkinlikleri]  = useState(() => _readDC(familyId, "calendar") || []);
  const [belgeler, setBelgeler]                       = useState(() => _readDC(familyId, "documents") || []);
  const [belgeYukleniyor, setBelgeYukleniyor]         = useState(false);
  const [belgeYuklemePct, setBelgeYuklemePct]         = useState(0);
  const [healthRecords, setHealthRecords]             = useState(() => areAndroidHealthFeaturesDisabled() ? [] : (_readDC(familyId, "health") || []));
  const [manualChildren, setManualChildren]           = useState(() => { if (areAndroidHealthFeaturesDisabled()) return []; const c = _readDC(familyId, "children"); return c?.children || []; });
  const [childRecords, setChildRecords]               = useState(() => { if (areAndroidHealthFeaturesDisabled()) return []; const c = _readDC(familyId, "children"); return c?.childRecords || []; });
  const [locations, setLocations] = useState({});
  const [locationsFamilyId, setLocationsFamilyId] = useState(null);
  const [mapMetricCounts, setMapMetricCounts] = useState(() => _readDC(familyId, "mapMetrics") || { places: 0, geofences: 0, safeZones: 0, locationPoints: 0 });
  const [okunmamisChat, setOkunmamisChat] = useState(0);
  const [okunmamisChatOda, setOkunmamisChatOda] = useState(0);
  const [toplamSohbet, setToplamSohbet] = useState(0);
  const [initialDataLoading, setInitialDataLoading] = useState(() => !!familyId && !_hasDashboardCache(familyId));
  // Günlük popup görev/takvim yanında sağlık verisini de kullandığı için bu kaynakların
  // ilk okuması tamamlanmadan popup hesaplanmamalıdır.
  const [popupDataLoading, setPopupDataLoading] = useState(() => {
    if (!familyId) return false;
    return !areAndroidHealthFeaturesDisabled() && _readDC(familyId, "health") === null;
  });
  const [popupDataFamilyId, setPopupDataFamilyId] = useState(() => familyId || null);
  const [initialDataFamilyId, setInitialDataFamilyId] = useState(() => familyId || null);
  const [initialDataUserUid, setInitialDataUserUid] = useState(() => currentUserUid || null);

  const upsertById = (list, item) => {
    if (!item?.id) return list;
    const exists = list.some((x) => x.id === item.id);
    return exists ? list.map((x) => (x.id === item.id ? { ...x, ...item } : x)) : [item, ...list];
  };

  const deleteById = (list, id) => list.filter((x) => x.id !== id);
  const enc = (v) => encodeURIComponent(String(v || ""));


  // FIX: targetUserUid + targetFamilyId parametreleri eklendi.
  // Çağrıldığı andaki uid ve familyId'yi closure'a bağımlı kalmadan alır.
  // Hesap değişiminde hook'un eski closure değerleri yerine güncel değerler kullanılır.
  const forceFreshNextLoad = (targetUserUid) => {
    forceFreshNextLoadRef.current = String(targetUserUid || currentUserUid || "").trim() || "*";
  };

  // Girişte hedef aile belirlenir belirlenmez o aileye ait son dashboard snapshot'ını
  // tek React turunda uygular. Böylece eski hesabın state'i görünmez ve beş REST
  // isteğinin bitmesi beklenmeden ana sayfa dolu açılır. Listener hemen ardından
  // sunucudan güncel veriyi sessizce yeniler.
  const hydrateDashboardCache = (targetFamilyId, targetUserUid) => {
    const activeFamilyId = String(targetFamilyId || "").trim();
    const activeUserUid = String(targetUserUid || currentUserUid || "").trim();
    if (!activeFamilyId) return false;

    const readFor = (resource) => _readDC(activeFamilyId, resource);
    const shopping = readFor("shopping");
    const tasks = readFor("tasks");
    const finance = readFor("finance");
    const sharedExpenses = readFor("sharedExpenses");
    const calendar = readFor("calendar");
    const documents = readFor("documents");
    const health = areAndroidHealthFeaturesDisabled() ? [] : readFor("health");
    const children = areAndroidHealthFeaturesDisabled() ? { children: [], childRecords: [] } : readFor("children");
    const mapMetrics = readFor("mapMetrics");

    const hasInitialCache = _hasDashboardCache(activeFamilyId);

    setUrunler(Array.isArray(shopping) ? shopping : []);
    setGorevler(Array.isArray(tasks) ? tasks : []);
    setFinanslar(Array.isArray(finance) ? finance : []);
    setHarcamalar(Array.isArray(sharedExpenses) ? sharedExpenses : []);
    setTakvimEtkinlikleri(Array.isArray(calendar) ? calendar : []);
    setBelgeler(Array.isArray(documents) ? documents : []);
    setHealthRecords(Array.isArray(health) ? health : []);
    setManualChildren(Array.isArray(children?.children) ? children.children : []);
    setChildRecords(Array.isArray(children?.childRecords) ? children.childRecords : []);
    setLocations({});
    setLocationsFamilyId(null);
    setOkunmamisChat(0);
    setOkunmamisChatOda(0);
    setToplamSohbet(0);
    setMapMetricCounts(
      mapMetrics && typeof mapMetrics === "object"
        ? mapMetrics
        : { places: 0, geofences: 0, safeZones: 0, locationPoints: 0 }
    );

    setInitialDataFamilyId(activeFamilyId);
    setInitialDataUserUid(activeUserUid || null);
    // Cache gerçekten tam ise ana sayfa anında açılabilir. Yeni kurulumda veya
    // eksik cache durumunda REST yüklemesi bitmeden sahte 0 değerleri gösterme.
    setInitialDataLoading(!hasInitialCache);
    setPopupDataFamilyId(activeFamilyId);
    setPopupDataLoading(!areAndroidHealthFeaturesDisabled() && health === null);
    return hasInitialCache;
  };

  const veriDinlemeBaslat = (targetUserUid, targetFamilyId) => {
    // activeFamilyId: parametre varsa onu kullan, yoksa hook closure'undaki familyId'ye dön
    const activeFamilyId = String(targetFamilyId || familyId || "").trim();
    if (!activeFamilyId) return () => {};

    if (areAndroidHealthFeaturesDisabled()) {
      try {
        removeSensitiveItem(`fp:dc:${activeFamilyId}:health`);
        removeSensitiveItem(`fp:dc:${activeFamilyId}:children`);
      } catch {}
    }

    // activeUserUid: parametre varsa onu kullan, yoksa hook closure'undaki currentUserUid'ye dön
    const activeUserUid = String(targetUserUid || currentUserUid || "").trim();
    const initialLoadKey = `${activeUserUid || "anonymous"}:${activeFamilyId}`;
    activeInitialLoadRef.current = initialLoadKey;
    // Konum ve sohbet state'i disk cache'inden hydrate edilmez. Önceki aileye
    // ait anlık veriyi yeni aile SSE'si gelene kadar taşımamak gerekir.
    setLocations({});
    setLocationsFamilyId(null);
    setOkunmamisChat(0);
    setOkunmamisChatOda(0);
    setToplamSohbet(0);

    // activeFamilyId'ye özel cache yardımcıları — closure familyId'sini KULLANMAZ
    const activeCacheKey = (resource) => `fp:dc:${activeFamilyId}:${resource}`;
    const readActiveCache = (resource) => {
      try {
        const r = readSensitiveItem(activeCacheKey(resource));
        return r ? JSON.parse(r) : null;
      } catch { return null; }
    };
    const writeActiveCache = (resource, data) => {
      try { writeSensitiveItem(activeCacheKey(resource), JSON.stringify(data)); } catch {}
    };

    const hasInitialCache = _hasDashboardCache(activeFamilyId);
    const freshMarker = forceFreshNextLoadRef.current;
    const requireFresh = freshMarker === "*" || (!!activeUserUid && freshMarker === activeUserUid);
    if (requireFresh) forceFreshNextLoadRef.current = null;

    setInitialDataFamilyId(activeFamilyId);
    setInitialDataUserUid(activeUserUid || null);
    setInitialDataLoading(requireFresh || !hasInitialCache);
    setPopupDataFamilyId(activeFamilyId);
    setPopupDataLoading(!areAndroidHealthFeaturesDisabled() && readActiveCache("health") === null);

    // Hesap değişiminde önceki kullanıcının React state'ini temizle.
    // Yeni hesabın cache'i varsa anında yükle; yoksa boş başlat.
    { const c = readActiveCache("shopping");       setUrunler(c || []); }
    { const c = readActiveCache("tasks");          setGorevler(c || []); }
    { const c = readActiveCache("finance");        setFinanslar(c || []); }
    { const c = readActiveCache("sharedExpenses"); setHarcamalar(c || []); }
    { const c = readActiveCache("calendar");       setTakvimEtkinlikleri(c || []); }
    { const c = areAndroidHealthFeaturesDisabled() ? null : readActiveCache("health"); setHealthRecords(c || []); }
    { const c = areAndroidHealthFeaturesDisabled() ? null : readActiveCache("children");
      setManualChildren(c?.children || []);
      setChildRecords(c?.childRecords || []);
    }

    let cancelled = false;
    const timers = [];
    const cleanups = [];
    const scheduleRecurring = (task, nextDelay) => {
      let timeoutId = null;
      let stopped = false;
      const schedule = () => {
        if (stopped || cancelled) return;
        timeoutId = window.setTimeout(() => {
          if (stopped || cancelled) return;
          task();
          schedule();
        }, nextDelay());
      };
      schedule();
      cleanups.push(() => {
        stopped = true;
        if (timeoutId !== null) window.clearTimeout(timeoutId);
      });
    };
    // Aynı kaynak için devam eden REST isteği varken ikinci bir kopya başlatma.
    // SSE, visibilitychange ve polling aynı ana denk gelse bile tek ağ isteği paylaşılır.
    const inFlightLoads = new Map();
    const runDeduped = (key, task) => {
      const existing = inFlightLoads.get(key);
      if (existing) return existing;
      const request = Promise.resolve()
        .then(task)
        .finally(() => {
          if (inFlightLoads.get(key) === request) inFlightLoads.delete(key);
        });
      inFlightLoads.set(key, request);
      return request;
    };

    // Başlangıçtaki ikincil GET isteklerini küçük gruplar halinde çalıştırır.
    // Bu sınır yalnızca bootstrap akışına aittir; SSE, chat, push ve kullanıcı
    // işlemleri global bir kuyruğa alınmaz.
    const allSettledLimited = async (tasks, limit = 2) => {
      const normalizedLimit = Math.max(1, Math.min(Number(limit) || 1, tasks.length || 1));
      const results = new Array(tasks.length);
      let nextIndex = 0;

      const worker = async () => {
        while (!cancelled) {
          const index = nextIndex;
          nextIndex += 1;
          if (index >= tasks.length) return;
          try {
            results[index] = { status: "fulfilled", value: await tasks[index]() };
          } catch (reason) {
            results[index] = { status: "rejected", reason };
          }
        }
      };

      await Promise.all(Array.from({ length: normalizedLimit }, () => worker()));
      return results;
    };

    const safeSet = (setter, value) => {
      if (!cancelled) setter(value);
    };

    // Kritik dashboard GET'leri ilk geçici ağ hatasında polling'i beklemez.
    // 500ms ve 1500ms civarında en fazla iki kontrollü tekrar yapılır.
    const criticalGet = (path, options = {}) => apiRequestWithRetry(path, options, {
      delays: [500, 1500],
      jitter: 120,
      shouldContinue: () => !cancelled && activeInitialLoadRef.current === initialLoadKey,
    });

    const loadShopping = () => runDeduped("shopping", async () => {
      try {
        const data = await criticalGet(`/shopping/${enc(activeFamilyId)}`);
        const items = Array.isArray(data?.items) ? data.items : [];
        shoppingAlindiDurumRef.current = Object.fromEntries(items.map((u) => [u.id, { alindi: u.alindi, alanUid: u.alanUid }]));
        safeSet(setUrunler, items);
        writeActiveCache("shopping", items);
      } catch (e) {
        if (isNetworkError(e)) { const c = readActiveCache("shopping"); if (c) safeSet(setUrunler, c); }
        else console.error("Alışveriş REST okunamadı:", e);
      }
    });

    const loadTasks = () => runDeduped("tasks", async () => {
      try {
        const data = await criticalGet(`/tasks/${enc(activeFamilyId)}`, { skipAndroidLiteHeader: true });
        const items = Array.isArray(data?.tasks) ? data.tasks : [];
        safeSet(setGorevler, items);
        writeActiveCache("tasks", items);
      } catch (e) {
        if (isNetworkError(e)) { const c = readActiveCache("tasks"); if (c) safeSet(setGorevler, c); }
        else console.error("Görev REST okunamadı:", e);
      }
    });

    const isMainFinanceItem = (item = {}) => {
      const type = item.itemType || item.item_type;
      return type === "income" || type === "expense" || item.tur === "Gelir" || item.tur === "Gider";
    };

    const loadFinance = () => runDeduped("finance", async () => {
      try {
        // Backend finance_items tablosu birikim/taksit/abonelik/faturayı da aynı tabloda tutuyor.
        // Ana finans ekranı yalnızca gelir/gider beklediği için burada özellikle income+expense çekiyoruz.
        const [incomeData, expenseData] = await Promise.all([
          criticalGet(`/finance/${enc(activeFamilyId)}?type=income`),
          criticalGet(`/finance/${enc(activeFamilyId)}?type=expense`),
        ]);
        const items = [
          ...(Array.isArray(incomeData?.items) ? incomeData.items : []),
          ...(Array.isArray(expenseData?.items) ? expenseData.items : []),
        ];
        items.sort((a, b) => new Date(b.tarih || b.createdAt || 0) - new Date(a.tarih || a.createdAt || 0));
        safeSet(setFinanslar, items);
        writeActiveCache("finance", items);
      } catch (e) {
        if (isNetworkError(e)) { const c = readActiveCache("finance"); if (c) safeSet(setFinanslar, c); }
        else console.error("Finans REST okunamadı:", e);
      }
    });

    const loadSharedExpenses = () => runDeduped("sharedExpenses", async () => {
      try {
        const data = await criticalGet(`/shared-expenses/${enc(activeFamilyId)}`);
        const items = Array.isArray(data?.items) ? data.items : [];
        safeSet(setHarcamalar, items);
        writeActiveCache("sharedExpenses", items);
      } catch (e) {
        if (isNetworkError(e)) { const c = readActiveCache("sharedExpenses"); if (c) safeSet(setHarcamalar, c); }
        else console.error("Ortak harcama REST okunamadı:", e);
      }
    });

    const loadCalendar = () => runDeduped("calendar", async () => {
      try {
        // Takvim Android'de açık bir ana modüldür. Sağlık sayfasına istek
        // göndermeden, Sağlık/Randevu kategorili normal takvim etkinliklerini de
        // listeleyebilmek için yalnızca bu takvim GET'inde Lite filtresini kaldır.
        const data = await criticalGet(`/calendar/${enc(activeFamilyId)}`, { skipAndroidLiteHeader: true });
        const items = Array.isArray(data?.events) ? data.events : [];
        safeSet(setTakvimEtkinlikleri, items);
        writeActiveCache("calendar", items);
      } catch (e) {
        if (isNetworkError(e)) { const c = readActiveCache("calendar"); if (c) safeSet(setTakvimEtkinlikleri, c); }
        else console.error("Takvim REST okunamadı:", e);
      }
    });

    const refreshMigrated = () => {
      // Sekme/uygulama arka plandayken sunucuyu boş yere yormamak için polling
      // atlanır. Öne gelince (focus/visibilitychange) zaten yeniden yüklenir.
      if (typeof document !== "undefined" && document.hidden) return;
      if (isDataSyncConnected(activeFamilyId)) return;
      loadShopping();
      loadTasks();
      loadFinance();
      loadSharedExpenses();
      loadCalendar();
    };

    // İlk yükleme paralel yapılır. Cache yoksa ana sayfa sahte "0" değerleri
    // göstermek yerine bu ilk çekim tamamlanana kadar yükleme durumunda kalır.
    const initialDashboardPromise = Promise.allSettled([
      loadShopping(),
      loadTasks(),
      loadFinance(),
      loadSharedExpenses(),
      loadCalendar(),
    ]).finally(() => {
      if (!cancelled && activeInitialLoadRef.current === initialLoadKey) {
        setInitialDataLoading(false);
      }
    });
    // SSE primary; yalnız bağlantı koptuğunda yaklaşık dakikalık, jitter'lı
    // polling güvenlik ağı çalışır. Böylece istemciler aynı saniyede yığılmaz.
    scheduleRecurring(refreshMigrated, nextDisconnectedSyncDelay);

    // Arka plandan öne dönünce alışverişi ÖNCELİKLİ ve anında tazele.
    // iOS WKWebView her dönüşte yalnızca visibilitychange üretmeyebildiği için
    // pageshow ve focus da güvenlik ağı olarak dinlenir. Aynı anda tetiklenirlerse
    // runDeduped ikinci ağ isteğini engeller.
    const refreshAfterResume = () => {
      if (typeof document !== "undefined" && document.hidden) return;
      loadShopping();
      // Alışveriş isteğinin bağlantı havuzunda öncelik alması için diğer temel
      // kaynakları bir sonraki kısa turda yenile.
      timers.push(window.setTimeout(() => {
        if (!cancelled) {
          loadTasks();
          loadFinance();
          loadSharedExpenses();
          loadCalendar();
        }
      }, 150));
    };
    const unsubscribeMigratedResume = subscribeAppResume(refreshAfterResume, { delayMs: 100 });

    const handleRestChange = (event) => {
      const d = event?.detail || {};
      if (d.familyId !== activeFamilyId) return;

      if (d.resource === "shopping") {
        if (d.action === "added") {
          setUrunler((prev) => upsertById(prev, d.item));
          if (!d.extra?.silentNotification && d.item?.ekleyenUid && d.item.ekleyenUid === user?.uid) {
            bildirimHerkeseYazService({
              familyId, uyeler: uyelerRef.current, ekleyenUid: d.item.ekleyenUid,
              baslik: msg("notify.shoppingListTitle"),
              aciklama: msg("notify.shoppingAdded", { name: d.item.ekleyen, item: d.item.ad }),
              baslikKey: "notify.shoppingListTitle", aciklamaKey: "notify.shoppingAdded", aciklamaVars: { name: d.item.ekleyen, item: d.item.ad },
              type: "shopping", kaynak: "shopping", kaynakId: d.item.id,
              idempotencyKey: `shopping_${d.item.id}`,
            }).catch(() => {});
          }
        }
        if (d.action === "bulkAdded") {
          const count = Number(d.extra?.count || d.extra?.total || 0);
          const ekleyenUid = d.extra?.ekleyenUid || user?.uid;
          const ekleyen = d.extra?.ekleyen || "";
          if (count > 0 && ekleyenUid && ekleyenUid === user?.uid) {
            bildirimHerkeseYazService({
              familyId, uyeler: uyelerRef.current, ekleyenUid,
              baslik: msg("notify.shoppingBulkTitle"),
              aciklama: msg("notify.shoppingBulkAdded", { name: ekleyen, count }),
              baslikKey: "notify.shoppingBulkTitle",
              aciklamaKey: "notify.shoppingBulkAdded",
              aciklamaVars: { name: ekleyen, count },
              type: "shopping",
              kaynak: "shopping",
              kaynakId: d.extra?.bulkBatchId || "",
              idempotencyKey: `shopping_bulk_${d.extra?.bulkBatchId || Date.now()}`,
            }).catch(() => {});
          }
        }
        if (d.action === "updated") setUrunler((prev) => upsertById(prev, d.item));
        if (d.action === "deleted") setUrunler((prev) => deleteById(prev, d.itemId));

        // Toggle/update yanıtı güncel öğeyi zaten içeriyor. Bu durumda 250 ms sonra
        // aynı alışveriş listesini yeniden istemek görünür davranışı değiştirmeden
        // gereksiz ikinci GET üretir. Ekleme/silme/bulk veya eksik payload durumunda
        // mevcut sunucu doğrulaması aynen korunur.
        const hasCompleteUpdatedItem = d.action === "updated" && d.item?.id;
        if (!hasCompleteUpdatedItem) window.setTimeout(loadShopping, 250);
        return;
      }

      if (d.resource === "tasks") {
        if (d.action === "added") {
          setGorevler((prev) => upsertById(prev, d.item));
          const g = d.item || {};
          if (g.ekleyenUid && g.ekleyenUid === user?.uid) {
            // Çoklu atama: atananUids varsa hepsine, yoksa tekil atananUid'e bildirim
            const aliciUids = Array.isArray(g.atananUids) && g.atananUids.length > 0
              ? g.atananUids
              : (g.atananUid ? [g.atananUid] : []);
            aliciUids.forEach(aliciUid => {
              if (aliciUid && aliciUid !== g.ekleyenUid) {
                bildirimEkleService({
                  familyId, aliciUid,
                  baslik: msg("notify.taskAssignedTitle"), aciklama: msg("notify.taskAssigned", { task: g.ad }),
                  baslikKey: "notify.taskAssignedTitle", aciklamaKey: "notify.taskAssigned", aciklamaVars: { task: g.ad },
                  type: "task", kaynak: g.ekleyen || null, kaynakId: g.id, gonderenUid: g.ekleyenUid,
                }).catch(() => {});
              }
            });
          }
        }
        if (d.action === "updated") {
          setGorevler((prev) => upsertById(prev, d.item));
          // Görev tamamlandıysa aile üyelerine bildirim gönder
          if (d.item?.tamamlandi === true && user?.uid) {
            const tamamlayan = uyelerRef.current?.find(
              (u) => (u.uid || u.firebase_uid) === user.uid
            );
            const tamamlayanAd = tamamlayan
              ? (`${tamamlayan.ad || ""} ${tamamlayan.soyad || ""}`.trim() || tamamlayan.displayName || "")
              : "";
            const gorevAdi = d.item?.ad || "";
            bildirimHerkeseYazService({
              familyId,
              uyeler: uyelerRef.current,
              ekleyenUid: user.uid, // tamamlayana gitmesin
              baslik: msg("notify.taskCompletedTitle"),
              aciklama: msg("notify.taskCompleted", { name: tamamlayanAd, task: gorevAdi }),
              baslikKey: "notify.taskCompletedTitle",
              aciklamaKey: "notify.taskCompleted",
              aciklamaVars: { name: tamamlayanAd, task: gorevAdi },
              type: "taskDone",
              kaynak: "task",
              kaynakId: d.item?.id || "",
              gonderenUid: user.uid,
            }).catch(() => {});
          }
        }
        if (d.action === "deleted") setGorevler((prev) => deleteById(prev, d.itemId));
        window.setTimeout(loadTasks, 250);
        return;
      }

      if (d.resource === "finance") {
        // financeToolsService de aynı backend tablosuna yazar; ana finans state'ine
        // birikim/taksit/abonelik/fatura karışırsa FinancePage render akışı bozulabiliyor.
        if (d.action === "added" || d.action === "updated") {
          if (isMainFinanceItem(d.item)) setFinanslar((prev) => upsertById(prev, d.item));
        }
        if (d.action === "added" && isMainFinanceItem(d.item)) {
          const f = d.item || {};
          if (!d.extra?.silentNotification && f.ekleyenUid && f.ekleyenUid === user?.uid) {
            const isIncome = (f.itemType || f.item_type) === "income" || f.tur === "Gelir";
            // Bu finans kaydı aslında bir ortak harcamadan doğduysa (sharedExpenseService.js
            // eşlik eden finance_items satırını ortakHarcama:true ile oluşturuyor),
            // başlığı "Yeni Gider" yerine "Ortak Harcama" yap. Bu hem doğru bağlamı
            // verir hem de sharedExpenses bloğundaki bildirimle çakışmayı (aynı işlem
            // için 2 ayrı push gitmesini) tamamen önler — sharedExpenses bloğu zaten
            // d.extra.financeItem doluyken kendi bildirimini atlıyor.
            const isSharedExpense = !!f.ortakHarcama;
            // Backend, ortak harcamadan doğan finans kaydının başlığına "Ortak Harcama: "
            // önekini sabit TR olarak ekliyor (FinancePage listesinde ayırt etmek için —
            // bu veri tarafı, dokunulmuyor). Bildirimde bu önek hem gereksiz tekrar
            // yaratıyordu (başlık zaten "Ortak Harcama" diyor) hem de EN kullanıcıya
            // TR metin sızdırıyordu — bildirime giderken temizliyoruz.
            const temizBaslik = isSharedExpense
              ? String(f.baslik || "").replace(/^Ortak Harcama:\s*/i, "")
              : f.baslik;
            bildirimHerkeseYazService({
              familyId, uyeler: uyelerRef.current, ekleyenUid: f.ekleyenUid,
              baslik: msg(isSharedExpense ? "notify.sharedExpenseTitle" : (isIncome ? "notify.newIncome" : "notify.newExpense")),
              aciklama: msg(isSharedExpense ? "notify.sharedExpenseAdded" : "notify.financeAdded", { name: f.ekleyen, title: temizBaslik, amount: tutarFormatla(f.tutar, f.paraBirimi) }),
              baslikKey: isSharedExpense ? "notify.sharedExpenseTitle" : (isIncome ? "notify.newIncome" : "notify.newExpense"),
              aciklamaKey: isSharedExpense ? "notify.sharedExpenseAdded" : "notify.financeAdded",
              aciklamaVars: { name: f.ekleyen, title: temizBaslik, amount: tutarFormatla(f.tutar, f.paraBirimi) },
              type: isSharedExpense ? "sharedExpense" : "finance", idempotencyKey: `finance_${f.id}`,
            }).catch(() => {});
          }
        }
        if (d.action === "deleted") setFinanslar((prev) => deleteById(prev, d.itemId));
        if (!d.extra?.linkedPayment) window.setTimeout(loadFinance, 250);
        return;
      }

      if (d.resource === "sharedExpenses") {
        if (d.action === "added" || d.action === "updated") setHarcamalar((prev) => upsertById(prev, d.item));
        if (d.action === "added") {
          const h = d.item || {};
          // sharedExpenseService.js bir ortak harcama eklendiğinde HEM "sharedExpenses"
          // HEM (varsa eşlik eden finans kaydı için) ayrı bir "finance" event'i fırlatıyor.
          // İkisi de bildirim tetikleseydi aynı işlem için kullanıcıya 2 ayrı push giderdi.
          const hasAccompanyingFinanceItem = !!(d.extra && d.extra.financeItem);
          if (!d.extra?.silentNotification && h.ekleyenUid && h.ekleyenUid === user?.uid && !hasAccompanyingFinanceItem) {
            bildirimHerkeseYazService({
              familyId, uyeler: uyelerRef.current, ekleyenUid: h.ekleyenUid,
              baslik: msg("notify.sharedExpenseTitle"),
              aciklama: msg("notify.sharedExpenseAdded", { name: h.ekleyen, title: h.baslik, amount: tutarFormatla(h.tutar, h.paraBirimi) }),
              baslikKey: "notify.sharedExpenseTitle",
              aciklamaKey: "notify.sharedExpenseAdded", aciklamaVars: { name: h.ekleyen, title: h.baslik, amount: tutarFormatla(h.tutar, h.paraBirimi) },
              type: "sharedExpense", idempotencyKey: `sharedExpense_${h.id}`,
            }).catch(() => {});
          }
        }
        if (d.action === "bulkAdded") {
          const count = Number(d.extra?.count || d.extra?.total || 0);
          const ekleyenUid = d.extra?.ekleyenUid || user?.uid;
          const ekleyen = d.extra?.ekleyen || "";
          if (count > 0 && ekleyenUid && ekleyenUid === user?.uid) {
            bildirimHerkeseYazService({
              familyId, uyeler: uyelerRef.current, ekleyenUid,
              baslik: msg("notify.receiptBulkTitle"),
              aciklama: msg("notify.receiptBulkAdded", { name: ekleyen, count }),
              baslikKey: "notify.receiptBulkTitle",
              aciklamaKey: "notify.receiptBulkAdded",
              aciklamaVars: { name: ekleyen, count },
              type: "sharedExpense",
              kaynak: "sharedExpense",
              kaynakId: d.extra?.bulkBatchId || "",
              idempotencyKey: `sharedExpense_bulk_${d.extra?.bulkBatchId || Date.now()}`,
            }).catch(() => {});
          }
        }
        if (d.action === "deleted") setHarcamalar((prev) => deleteById(prev, d.itemId));
        window.setTimeout(loadSharedExpenses, 250);
        return;
      }

      if (d.resource === "calendar") {
        if (d.action === "added") {
          setTakvimEtkinlikleri((prev) => upsertById(prev, d.item));
          const e = d.item || {};
          if (e.ekleyenUid && e.ekleyenUid === user?.uid) {
            bildirimHerkeseYazService({
              familyId, uyeler: uyelerRef.current, ekleyenUid: e.ekleyenUid,
              baslik: msg("notify.calendarEventTitle"),
              aciklama: msg("notify.calendarEventAdded", { name: e.ekleyenAd || e.ekleyen, event: e.baslik || e.ad }),
              baslikKey: "notify.calendarEventTitle", aciklamaKey: "notify.calendarEventAdded",
              aciklamaVars: { name: e.ekleyenAd || e.ekleyen, event: e.baslik || e.ad },
              type: "calendar", idempotencyKey: `calendar_${e.id}`,
            }).catch(() => {});
          }
        }
        if (d.action === "updated") setTakvimEtkinlikleri((prev) => upsertById(prev, d.item));
        if (d.action === "deleted") setTakvimEtkinlikleri((prev) => deleteById(prev, d.itemId));
        window.setTimeout(loadCalendar, 250);
      }
    };

    window.addEventListener("fp:restDataChanged", handleRestChange);
    cleanups.push(() => window.removeEventListener("fp:restDataChanged", handleRestChange));
    cleanups.push(unsubscribeMigratedResume);

    // ── SSE: Diğer kullanıcıların değişikliklerini anlık al ──────────────────
    // Backend /family/:familyId/data-stream endpoint'i her veri değişikliğinde
    // bağlı tüm aile üyelerine push gönderir. Bu sayede polling 60s'ye çıkarıldı.
    const unsubDataSync = dataSyncService(activeFamilyId, ({ resource, action, item, itemId, extra }) => {
      // Kendi işlemlerimiz zaten emitRestDataChanged ile LOCAL olarak işlendi.
      // SSE'den gelen event diğer kullanıcıların değişikliklerini taşır.
      // Güvenli yol: her resource için ilgili load fonksiyonunu çağır.
      if (cancelled) return;
      if (resource === "mapLive" && item) {
        let metricPatch = null;
        if (item.type === "places_updated" && Array.isArray(item.places)) {
          metricPatch = { places: item.places.length };
        } else if (item.type === "geofences_updated" && Array.isArray(item.geofences)) {
          metricPatch = { geofences: item.geofences.length };
        } else if (item.type === "safe_zones_updated" && Array.isArray(item.safeZones)) {
          metricPatch = { safeZones: item.safeZones.length };
        }
        if (metricPatch) {
          safeSet(setMapMetricCounts, (prev) => {
            const next = { ...(prev || {}), ...metricPatch };
            writeActiveCache("mapMetrics", next);
            return next;
          });
        }
        return;
      }
      if (resource === "familyMembers") {
        window.dispatchEvent(new CustomEvent("fp:familyPermissionsChanged", {
          detail: { familyId: activeFamilyId, action, targetUid: extra?.targetUid || itemId || null },
        }));
        return;
      }
      const resourceMap = {
        shopping:       loadShopping,
        tasks:          loadTasks,
        finance:        loadFinance,
        sharedExpenses: loadSharedExpenses,
        calendar:       loadCalendar,
        ...(!areAndroidHealthFeaturesDisabled() ? {
          health:       loadHealth,
          children:     loadChildrenBundle,
          childRecords: loadChildrenBundle,
        } : {}),
      };
      const loadFn = resourceMap[resource];
      if (loadFn) {
        let payloadUygulandi = false;
        const listePayloadiniUygula = (setter, cacheKey, updater) => {
          safeSet(setter, (prev) => {
            const next = updater(prev);
            writeActiveCache(cacheKey, next);
            return next;
          });
          payloadUygulandi = true;
        };
        // Tam payload varsa state'i doğrudan güncelle; eksik/eski backend
        // olaylarında güvenli tam liste GET fallback'i aşağıda korunur.
        if (item && action === "added") {
          if (resource === "shopping")       listePayloadiniUygula(setUrunler, "shopping", (prev) => upsertById(prev, item));
          if (resource === "tasks")          listePayloadiniUygula(setGorevler, "tasks", (prev) => upsertById(prev, item));
          if (resource === "calendar")       listePayloadiniUygula(setTakvimEtkinlikleri, "calendar", (prev) => upsertById(prev, item));
          if (resource === "sharedExpenses") listePayloadiniUygula(setHarcamalar, "sharedExpenses", (prev) => upsertById(prev, item));
          if (resource === "health")         listePayloadiniUygula(setHealthRecords, "health", (prev) => upsertById(prev, item));
          if (resource === "children")       { safeSet(setManualChildren, (prev) => upsertById(prev, { ...item, sourceType: item.sourceType || "manual" })); payloadUygulandi = true; }
          if (resource === "childRecords")   { safeSet(setChildRecords, (prev) => upsertById(prev, item)); payloadUygulandi = true; }
          if (resource === "finance" && isMainFinanceItem(item)) listePayloadiniUygula(setFinanslar, "finance", (prev) => upsertById(prev, item));
        }
        if (item && action === "updated") {
          if (resource === "shopping")       listePayloadiniUygula(setUrunler, "shopping", (prev) => upsertById(prev, item));
          if (resource === "tasks")          listePayloadiniUygula(setGorevler, "tasks", (prev) => upsertById(prev, item));
          if (resource === "calendar")       listePayloadiniUygula(setTakvimEtkinlikleri, "calendar", (prev) => upsertById(prev, item));
          if (resource === "sharedExpenses") listePayloadiniUygula(setHarcamalar, "sharedExpenses", (prev) => upsertById(prev, item));
          if (resource === "health")         listePayloadiniUygula(setHealthRecords, "health", (prev) => upsertById(prev, item));
          if (resource === "children")       { safeSet(setManualChildren, (prev) => upsertById(prev, { ...item, sourceType: item.sourceType || "manual" })); payloadUygulandi = true; }
          if (resource === "childRecords")   { safeSet(setChildRecords, (prev) => upsertById(prev, item)); payloadUygulandi = true; }
          if (resource === "finance" && isMainFinanceItem(item)) listePayloadiniUygula(setFinanslar, "finance", (prev) => upsertById(prev, item));
        }
        if (itemId && action === "deleted") {
          if (resource === "shopping")       listePayloadiniUygula(setUrunler, "shopping", (prev) => deleteById(prev, itemId));
          if (resource === "tasks")          listePayloadiniUygula(setGorevler, "tasks", (prev) => deleteById(prev, itemId));
          if (resource === "calendar")       listePayloadiniUygula(setTakvimEtkinlikleri, "calendar", (prev) => deleteById(prev, itemId));
          if (resource === "sharedExpenses") listePayloadiniUygula(setHarcamalar, "sharedExpenses", (prev) => deleteById(prev, itemId));
          if (resource === "health")         listePayloadiniUygula(setHealthRecords, "health", (prev) => deleteById(prev, itemId));
          if (resource === "children")       { safeSet(setManualChildren, (prev) => deleteById(prev, itemId)); payloadUygulandi = true; }
          if (resource === "childRecords")   { safeSet(setChildRecords, (prev) => deleteById(prev, itemId)); payloadUygulandi = true; }
          if (resource === "finance")        listePayloadiniUygula(setFinanslar, "finance", (prev) => deleteById(prev, itemId));
        }
        if (!payloadUygulandi && !(resource === "finance" && extra?.linkedPayment)) {
          window.setTimeout(loadFn, 300);
        }
      }
    });
    cleanups.push(unsubDataSync);

    const handleDataReconcile = (event) => {
      if (cancelled || String(event?.detail?.familyId || "") !== String(activeFamilyId)) return;
      loadShopping();
      loadTasks();
      loadFinance();
      loadSharedExpenses();
      loadCalendar();
      loadMapMetricCounts();
      if (!areAndroidHealthFeaturesDisabled()) {
        loadHealth();
        loadChildrenBundle();
      }
    };
    window.addEventListener("fp:dataReconcile", handleDataReconcile);
    cleanups.push(() => window.removeEventListener("fp:dataReconcile", handleDataReconcile));

    const unsubDocuments = belgeleriDinleService(activeFamilyId, (yeniBelgeler) => {
      if (cancelled) return;
      // Başarılı veri gelince cache'e yaz
      if (Array.isArray(yeniBelgeler) && yeniBelgeler.length > 0) {
        writeActiveCache("documents", yeniBelgeler);
      }
      // Bu callback polling/login/resume hydration yoludur ve yalnız state/cache
      // günceller. İş bildirimi başarılı belge oluşturma mutation'ında üretilir.
      setBelgeler(yeniBelgeler);
    });
    // Belgeler SSE offline'da çalışmaz; cache varsa hemen yükle (SSE gelince override olur)
    { const c = readActiveCache("documents"); if (c) setBelgeler(c); }

    const loadHealth = () => runDeduped("health", async () => {
      if (areAndroidHealthFeaturesDisabled()) return;
      try {
        const data = await apiRequest(`/health-records/${enc(activeFamilyId)}`);
        const items = Array.isArray(data?.records) ? data.records : [];
        safeSet(setHealthRecords, items);
        writeActiveCache("health", items);
      } catch (e) {
        if (isNetworkError(e)) { const c = readActiveCache("health"); if (c) safeSet(setHealthRecords, c); }
        else console.error("Sağlık REST okunamadı:", e);
      }
    });

    const loadChildrenBundle = () => runDeduped("children", async () => {
      if (areAndroidHealthFeaturesDisabled()) return;
      try {
        const data = await apiRequest(`/children/${enc(activeFamilyId)}`);
        const children = Array.isArray(data?.children) ? data.children.map((c) => ({ ...c, sourceType: c.sourceType || "manual" })) : [];
        const childRecs = Array.isArray(data?.childRecords) ? data.childRecords : [];
        safeSet(setManualChildren, children);
        safeSet(setChildRecords, childRecs);
        writeActiveCache("children", { children, childRecords: childRecs });
      } catch (e) {
        if (isNetworkError(e)) {
          const c = readActiveCache("children");
          if (c) { safeSet(setManualChildren, c.children || []); safeSet(setChildRecords, c.childRecords || []); }
        } else console.error("Çocuk REST okunamadı:", e);
      }
    });

    const refreshRestOnly = () => {
      // Arka plandayken polling atlanır (bkz. refreshMigrated).
      if (typeof document !== "undefined" && document.hidden) return;
      if (isDataSyncConnected(activeFamilyId)) return;
      if (areAndroidHealthFeaturesDisabled()) return;
      loadHealth();
      loadChildrenBundle();
    };

    // İlk sağlık/çocuk okumasını takip et. Günlük popup bu Promise tamamlanmadan
    // açılmaz; böylece boş state üzerinden "gösterildi" kaydı yazılmaz.
    // Kritik dashboard tamamlandıktan sonra ikincil kaynaklar en fazla iki paralel
    // istekle yüklenir. Bu, mobil WKWebView bağlantı havuzunu korurken popup'ın
    // sağlık/çocuk doğruluğunu aynen sürdürür.
    const secondaryCorePromise = initialDashboardPromise
      .then(() => areAndroidHealthFeaturesDisabled()
        ? []
        : allSettledLimited([
            () => loadHealth(),
            () => loadChildrenBundle(),
          ], 2))
      .finally(() => {
        if (!cancelled) {
          setPopupDataFamilyId(activeFamilyId);
          setPopupDataLoading(false);
        }
      });
    // SSE primary; polling güvenlik ağı olarak 60sn'de bir çalışır
    if (!areAndroidHealthFeaturesDisabled()) {
      timers.push(window.setInterval(refreshRestOnly, 60000));
    }

    const unsubscribeRestResume = subscribeAppResume(refreshRestOnly, { delayMs: 300 });

    const handleRestOnlyChange = (event) => {
      const d = event?.detail || {};
      if (d.familyId !== activeFamilyId) return;
      if (isAndroidRestrictedModule(d.resource)) return;
      if (d.resource === "health" || d.resource === "healthRecords") {
        if (d.action === "added") {
          setHealthRecords((prev) => upsertById(prev, d.item));
          // Kişisel sağlık kartına kayıt eklenmesi aile etkinliği değildir.
          // İlaç/randevu hatırlatmaları backend tarafından yalnız kayıt
          // sahibine gönderilir; burada aile geneline bildirim üretilmez.
        }
        if (d.action === "updated") setHealthRecords((prev) => upsertById(prev, d.item));
        if (d.action === "deleted") setHealthRecords((prev) => deleteById(prev, d.itemId));
        if (!d.item && !d.itemId) window.setTimeout(loadHealth, 250);
      }
      if (d.resource === "children") {
        if (d.action === "added" || d.action === "updated") setManualChildren((prev) => upsertById(prev, { ...(d.item || {}), sourceType: "manual" }));
        if (d.action === "deleted") setManualChildren((prev) => deleteById(prev, d.itemId));
        if (!d.item && !d.itemId) window.setTimeout(loadChildrenBundle, 250);
      }
      if (d.resource === "childRecords") {
        if (d.action === "added") {
          setChildRecords((prev) => upsertById(prev, d.item));
          const c = d.item || {};
          if (c.ekleyenUid && c.ekleyenUid === user?.uid) {
            bildirimHerkeseYazService({
              familyId, uyeler: uyelerRef.current, ekleyenUid: c.ekleyenUid,
              baslik: msg("notify.childRecordTitle"),
              aciklama: msg("notify.childRecordAdded", { name: c.ekleyen, title: c.title }),
              baslikKey: "notify.childRecordTitle",
              aciklamaKey: "notify.childRecordAdded", aciklamaVars: { name: c.ekleyen, title: c.title },
              type: "children", idempotencyKey: `childRecord_${c.id}`,
            }).catch(() => {});
          }
        }
        if (d.action === "updated") setChildRecords((prev) => upsertById(prev, d.item));
        if (d.action === "deleted") setChildRecords((prev) => deleteById(prev, d.itemId));
        if (!d.item && !d.itemId) window.setTimeout(loadChildrenBundle, 250);
      }
    };

    window.addEventListener("fp:restDataChanged", handleRestOnlyChange);
    cleanups.push(() => window.removeEventListener("fp:restDataChanged", handleRestOnlyChange));
    cleanups.push(unsubscribeRestResume);

    const loadMapMetricCounts = () => runDeduped(
      "mapMetrics",
      async () => {
        try {
          const [placesRes, geofencesRes, safeZonesRes, historyCountRes] = await allSettledLimited([
            () => apiRequest(`/map/${enc(activeFamilyId)}/places`),
            () => apiRequest(`/map/${enc(activeFamilyId)}/geofences`),
            () => apiRequest(`/map/${enc(activeFamilyId)}/safe-zones`),
            () => apiRequest(`/map/${enc(activeFamilyId)}/location-history/count`),
          ], 2);
          const placesData = placesRes.status === "fulfilled" ? placesRes.value : null;
          const geofencesData = geofencesRes.status === "fulfilled" ? geofencesRes.value : null;
          const safeZonesData = safeZonesRes.status === "fulfilled" ? safeZonesRes.value : null;
          const historyData = historyCountRes.status === "fulfilled" ? historyCountRes.value : null;
          const cachedMetrics = readActiveCache("mapMetrics");

          const hasValidHistoryCount =
            historyCountRes.status === "fulfilled" &&
            Number.isFinite(Number(historyData?.count)) &&
            Number(historyData.count) >= 0;

          const next = {
            places: placesRes.status === "fulfilled"
              ? (Array.isArray(placesData?.places) ? placesData.places.length : 0)
              : Number(cachedMetrics?.places || 0),
            geofences: geofencesRes.status === "fulfilled"
              ? (Array.isArray(geofencesData?.geofences) ? geofencesData.geofences.length : 0)
              : Number(cachedMetrics?.geofences || 0),
            safeZones: safeZonesRes.status === "fulfilled"
              ? (Array.isArray(safeZonesData?.safeZones) ? safeZonesData.safeZones.length : 0)
              : Number(cachedMetrics?.safeZones || 0),
            locationPoints: hasValidHistoryCount
              ? Number(historyData.count)
              : Number(cachedMetrics?.locationPoints || 0),
          };
          safeSet(setMapMetricCounts, next);
          writeActiveCache("mapMetrics", next);
        } catch (_) {}
      }
    );

    const loadLocationPointCount = () => runDeduped(
      "mapHistoryCount",
      async () => {
        try {
          const historyData = await apiRequest(`/map/${enc(activeFamilyId)}/location-history/count`);
          const count = Number(historyData?.count);
          if (!Number.isFinite(count) || count < 0) return;
          safeSet(setMapMetricCounts, (prev) => {
            const next = { ...(prev || {}), locationPoints: count };
            writeActiveCache("mapMetrics", next);
            return next;
          });
        } catch (_) {}
      }
    );

    { const c = readActiveCache("mapMetrics"); if (c) setMapMetricCounts(c); }
    // Ana açılışta üye x 15 gün geçmiş taraması yapılmaz.
    // Sağlık/çocuk bootstrap'ı tamamlandıktan sonra harita sayaçları yüklenir;
    // böylece kritik dashboard ve günlük popup verileriyle bağlantı yarışı oluşmaz.
    // Count endpoint hata verirse cache korunur. SSE bağlıyken yer/geofence/
    // güvenli alan sayıları yukarıdaki mapLive payload'ından güncellenir; yalnız
    // kullanıcıya özel geçmiş sayısı küçük count endpoint'iyle doğrulanır.
    secondaryCorePromise.finally(() => {
      if (!cancelled) loadMapMetricCounts();
    });
    // SSE bağlıyken yalnız kullanıcıya özel geçmiş sayısını seyrek uzlaştır.
    // Harita mutation payload'ları diğer sayaçları anında günceller. Bağlantı
    // yoksa eski güvenlik davranışı yaklaşık dakikada bir sürer.
    scheduleRecurring(
      () => {
        if (isDataSyncConnected(activeFamilyId)) loadLocationPointCount();
        else loadMapMetricCounts();
      },
      () => isDataSyncConnected(activeFamilyId)
        ? nextConnectedReconcileDelay()
        : nextDisconnectedSyncDelay()
    );

    const unsubLocations = konumlariDinleService(activeFamilyId, (payload) => {
      if (cancelled) return;
      setLocationsFamilyId(activeFamilyId);
      setLocations((prev) => {
        if (typeof payload === "function") {
          const next = payload(prev || {});
          return next && typeof next === "object" ? next : (prev || {});
        }
        return payload && typeof payload === "object" ? payload : {};
      });
    });

    let ilkChatRooms = true;
    const unsubChatRooms = odalariDinleService(activeFamilyId, user?.uid, (odalar = []) => {
      if (cancelled) return;
      const toplamMesaj = odalar.reduce((acc, o) => acc + ((o.okunmamisSayac || {})[user?.uid] || 0), 0);
      const okunmamisOdaSayisi = odalar.filter((o) => ((o.okunmamisSayac || {})[user?.uid] || 0) > 0).length;
      setOkunmamisChat(toplamMesaj);
      setOkunmamisChatOda(okunmamisOdaSayisi);
      setToplamSohbet(Array.isArray(odalar) ? odalar.length : 0);

      // Chat mesajı için bildirim merkezi + push artık backend chat.routes.js tarafından üretiliyor.
      // Burada sadece okunmamış sayaç tutulur; çift in-app/push üretimini engellemek için bildirim yazılmaz.
      if (ilkChatRooms) ilkChatRooms = false;
    });

    return () => {
      cancelled = true;
      timers.forEach((t) => window.clearInterval(t));
      cleanups.forEach((fn) => fn());
      unsubDocuments(); unsubLocations(); unsubChatRooms();
    };
  };

  return {
    urunler, setUrunler,
    alisverisKategorileri, setAlisverisKategorileri,
    gorevler, setGorevler,
    finanslar, setFinanslar,
    harcamalar, setHarcamalar,
    takvimEtkinlikleri, setTakvimEtkinlikleri,
    belgeler, setBelgeler,
    belgeYukleniyor, setBelgeYukleniyor,
    belgeYuklemePct, setBelgeYuklemePct,
    healthRecords, setHealthRecords,
    manualChildren, setManualChildren,
    childRecords, setChildRecords,
    locations, setLocations, locationsFamilyId, setLocationsFamilyId,
    mapMetricCounts, setMapMetricCounts,
    okunmamisChat, setOkunmamisChat,
    okunmamisChatOda, setOkunmamisChatOda,
    toplamSohbet, setToplamSohbet,
    initialDataLoading,
    initialDataFamilyId,
    initialDataUserUid,
    popupDataLoading,
    popupDataFamilyId,
    shoppingAlindiDurumRef,
    veriDinlemeBaslat,
    forceFreshNextLoad,
    hydrateDashboardCache,
  };
}
